<template>
  <div class="sidebar-container" :class="{ 'sidebar-collapsed': !sidebarExpanded }">
    <div class="sidebar" :class="{ 'sidebar-collapsed': !sidebarExpanded }">
      <div class="sidebar--logo">
        <template v-if="sidebarExpanded"><Logo /></template>
        <template v-if="!sidebarExpanded"><LogoAbridged /></template>

        <Button @click="toggleSidebar()" severity="sidebar">
          <IconSidebarCollapse v-if="sidebarExpanded" />
          <IconSidebarExpand v-if="!sidebarExpanded" />
        </Button>
      </div>

      <ul class="sidebar--links">
        <!-- Overview -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'overview' }"
          data-step="0"
        >
          <RouterLink to="/overview">
            <IconOverview />
            <span class="sidebar-link-title">Overview</span>
          </RouterLink>
        </li>

        <!-- Scorecard -->
        <li
          v-if="!userStore.isOperationsUser && userStore.hasFeature('scorecard')"
          :class="{ 'sidebar-link--active': routeMenu === 'scorecard' }"
        >
          <RouterLink to="/scorecard">
            <IconScorecard />
            <span class="sidebar-link-title">Scorecard</span>
          </RouterLink>
        </li>

        <!-- Events -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'events' }"
          data-step="4"
        >
          <RouterLink v-if="userStore.hasScope('events.show')" to="/events">
            <IconCalendar />
            <span class="sidebar-link-title">Events</span>
          </RouterLink>
          <Tooltip v-else position="right">
            <template v-slot:text>
              <span class="sidebar-link--disabled">
                <IconCalendar />
                <span class="sidebar-link-title">Events</span>
              </span>
            </template>
            <template v-slot:tooltip> You do not have access to view this page. </template>
          </Tooltip>
        </li>

        <!-- Event Booking -->
        <FeatureFlag name="event_booking">
          <FeatureFlag name="client_profile">
            <li
              v-if="!userStore.isOperationsUser"
              class="with-submenu"
              :class="{ 'sidebar-link--active': (activeMenu || routeMenu) === 'booking' }"
            >
              <div @click="setMenu('booking')" class="sidebar-link--header">
                <IconBooking />
                <span class="sidebar-link-title">Booking</span>
                <IconCaretUp v-if="activeMenu === 'booking'" class="sidebar-header-collapse" />
              </div>

              <div class="submenu">
                <ul>
                  <li>
                    <RouterLink to="/booking/new">
                      <span class="sidebar-link-title">New Quote</span>
                    </RouterLink>
                  </li>
                  <li>
                    <RouterLink to="/booking/drafts">
                      <span class="sidebar-link-title">Saved Quotes</span>
                    </RouterLink>
                  </li>
                  <li>
                    <RouterLink to="/booking/active">
                      <span class="sidebar-link-title">Active Bookings</span>
                    </RouterLink>
                  </li>
                </ul>
              </div>
            </li>
          </FeatureFlag>
        </FeatureFlag>

        <!-- Sensors -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'sensors' }"
          data-step="7"
        >
          <RouterLink v-if="userStore.hasScope('sensors.show')" to="/sensors">
            <IconSensors />
            <span class="sidebar-link-title">Sensors</span>
          </RouterLink>
          <Tooltip v-else position="right">
            <template v-slot:text>
              <span class="sidebar-link--disabled">
                <IconSensors />
                <span class="sidebar-link-title">Sensors</span>
              </span>
            </template>
            <template v-slot:tooltip> You do not have access to view this page. </template>
          </Tooltip>
        </li>

        <!-- Live Monitor -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'live' }">
          <RouterLink to="/sensors/live">
            <IconLive />
            <span class="sidebar-link-title">Live Monitor</span>
          </RouterLink>
        </li>

        <!-- Users -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'users' }"
          data-step="6"
        >
          <RouterLink v-if="userStore.hasScope('users.show')" to="/users">
            <IconUsers />
            <span class="sidebar-link-title">Users</span>
          </RouterLink>
          <Tooltip v-else position="right">
            <template v-slot:text>
              <span class="sidebar-link--disabled">
                <IconUsers />
                <span class="sidebar-link-title">Users</span>
              </span>
            </template>
            <template v-slot:tooltip> You do not have access to view this page. </template>
          </Tooltip>
        </li>

        <!-- Accounts -->
        <li
          v-if="userStore.hasScope('accounts.show') && userStore.isStaff"
          class="with-submenu"
          :class="{ 'sidebar-link--active': (activeMenu || routeMenu) === 'accounts' }"
        >
          <div @click="setMenu('accounts')" class="sidebar-link--header">
            <IconAccounts />
            <span class="sidebar-link-title">Accounts</span>
            <IconCaretUp v-if="activeMenu === 'accounts'" class="sidebar-header-collapse" />
          </div>

          <div class="submenu">
            <ul>
              <li>
                <RouterLink to="/accounts">
                  <IconEdit />
                  <span class="sidebar-link-title">Manage Accounts</span>
                </RouterLink>
              </li>
              <li v-if="userStore.isStaff">
                <RouterLink to="/roles">
                  <IconEdit />
                  <span class="sidebar-link-title">Manage Roles</span>
                </RouterLink>
              </li>
            </ul>
          </div>
        </li>

        <!-- Admin -->
        <FeatureFlag name="client_profile">
          <li
            v-if="userStore.isClientAdmin"
            class="with-submenu"
            :class="{ 'sidebar-link--active': (activeMenu || routeMenu) === 'admin' }"
          >
            <div @click="setMenu('admin')" class="sidebar-link--header">
              <IconAdmin />
              <span class="sidebar-link-title">Admin</span>
              <IconCaretUp v-if="activeMenu === 'admin'" class="sidebar-header-collapse" />
            </div>

            <div class="submenu">
              <ul>
                <li>
                  <RouterLink :to="`/admin/profile/${userStore.userData?.account_id}`">
                    <IconProfile />
                    <span class="sidebar-link-title">Profile</span>
                  </RouterLink>
                </li>
                <li>
                  <RouterLink to="/admin/documents">
                    <IconDocument />
                    <span class="sidebar-link-title">Documents</span>
                  </RouterLink>
                </li>
              </ul>
            </div>
          </li>
        </FeatureFlag>
      </ul>

      <ul class="sidebar--links sidebar--links-extra">
        <!-- FAQ -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'faq' }" data-step="8">
          <RouterLink to="/faq">
            <IconFAQ />
            <span class="sidebar-link-title">FAQ</span>
          </RouterLink>
        </li>

        <!-- API -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'api' }">
          <RouterLink to="/api">
            <IconAPI />
            <span class="sidebar-link-title">API</span>
          </RouterLink>
        </li>

        <!-- Contact -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'contact' }">
          <RouterLink to="/contact">
            <IconContact />
            <span class="sidebar-link-title">Contact</span>
          </RouterLink>
        </li>
      </ul>

      <UserMenu position="top">
        <template #button>
          <div class="sidebar--user-info" data-step="9">
            <div class="sidebar--user-info--logo">
              <img v-if="getAccountLogo != ''" :src="getAccountLogo" />
            </div>
            <div class="sidebar--user-info--content">
              <div class="sidebar--user-info--name">
                {{ userStore.userData?.name }}
              </div>
              <div class="sidebar--user-info--role">
                {{ userStore.userData?.role_name }}
              </div>
            </div>
            <IconCaretDown />
          </div>
        </template>
      </UserMenu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import Button from '@/components/global/Button.vue';
import UserMenu from '@/components/global/UserMenu.vue';
import Tooltip from './Tooltip.vue';

import Logo from '@/icons/Logo.svg?component';
import LogoAbridged from '@/icons/LogoAbridged.svg?component';

import IconSidebarCollapse from '@/icons/sidebar/IconSidebarCollapse.svg?component';
import IconSidebarExpand from '@/icons/sidebar/IconSidebarExpand.svg?component';
import IconAPI from '@/icons/sidebar/IconAPI.svg?component';
import IconAdmin from '@/icons/sidebar/IconAdmin.svg?component';
import IconAccounts from '@/icons/sidebar/IconAccounts.svg?component';
import IconBooking from '@/icons/sidebar/IconBooking.svg?component';
import IconCalendar from '@/icons/sidebar/IconCalendar.svg?component';
import IconCaretUp from '@/icons/arrows/IconCaretUp.svg?component';
import IconCaretDown from '@/icons/arrows/IconCaretDown.svg?component';
import IconContact from '@/icons/sidebar/IconContact.svg?component';
import IconDocument from '@/icons/IconDocument.svg?component';
import IconEdit from '@/icons/IconEdit.svg?component';
import IconFAQ from '@/icons/sidebar/IconFAQ.svg?component';
import IconLive from '@/icons/sidebar/IconLive.svg?component';
import IconOverview from '@/icons/sidebar/IconOverview.svg?component';
import IconProfile from '@/icons/UserProfile.svg?component';
import IconScorecard from '@/icons/sidebar/IconScorecard.svg?component';
import IconSensors from '@/icons/sidebar/IconSensors.svg?component';
import IconUsers from '@/icons/sidebar/IconUsers.svg?component';

import { useUserStore } from '@/stores/user';
import FeatureFlag from './FeatureFlag.vue';
const userStore = useUserStore();

const route = useRoute();
const activeMenu = ref('');
const routeMenu = ref('');

const setMenu = (menu: string) => {
  if (activeMenu.value === menu) {
    activeMenu.value = '';
  } else {
    activeMenu.value = menu;
  }
};

if (route.path === '/') {
  routeMenu.value = userStore.hasFeature('scorecard') ? 'scorecard' : 'overview';
} else if (route.path === '/overview') {
  routeMenu.value = 'overview';
} else if (route.path.includes('scorecard')) {
  routeMenu.value = 'scorecard';
} else if (route.path.includes('purchasing') || route.path.includes('booking')) {
  routeMenu.value = 'booking';
} else if (route.path.includes('events')) {
  routeMenu.value = 'events';
} else if (route.path.includes('live')) {
  routeMenu.value = 'live';
} else if (route.path.includes('sensors')) {
  routeMenu.value = 'sensors';
} else if (route.path.includes('users')) {
  routeMenu.value = 'users';
} else if (route.path.includes('admin')) {
  routeMenu.value = 'admin';
} else if (route.path.includes('accounts') || route.path.includes('roles')) {
  routeMenu.value = 'accounts';
} else if (route.path.includes('faq')) {
  routeMenu.value = 'faq';
} else if (route.path.includes('api')) {
  routeMenu.value = 'api';
} else if (route.path.includes('contact')) {
  routeMenu.value = 'contact';
}

const sidebarExpanded = ref(true);
const toggleSidebar = () => {
  sidebarExpanded.value = !sidebarExpanded.value;
};

const getAccountLogo = computed(() => {
  if (userStore.userData?.account_logo) {
    return `/media/${userStore.userData.account_logo}`;
  }
  return '';
});
</script>

<style lang="scss">
@use '@/assets/components/sidebar';
</style>
